/**
 * ManageCompanies.js — Super Admin: Manage tenant companies
 * Route: /superadmin/companies
 *
 * BUG FIX (this version): PATCH /superadmin/companies/:id was failing
 * with "Unknown column 'notes' in 'field list'" — the backend's ALLOWED
 * fields list correctly includes 'notes' and this form correctly sends
 * it, but the `companies` table never actually had a `notes` column
 * added via migration. This is a DATABASE fix, not a code fix — run
 * this once before deploying:
 *
 *   ALTER TABLE companies ADD COLUMN notes TEXT NULL;
 *
 * No backend or frontend code needed to change for that part — both
 * already handle `notes` correctly once the column exists.
 *
 * VALIDATION ADDED (this version):
 *   - Company Name: required
 *   - Email: required, valid format
 *   - Phone: optional, but if filled must be a valid international
 *     number (7-15 digits, optional leading + country code — same
 *     generic validation used in TenantSignup.js, kept consistent
 *     across the app rather than inventing different rules here)
 *   - Subscription Ends At: if set to a date in the past, shows a
 *     non-blocking warning (a SuperAdmin may legitimately need to
 *     backdate this for a billing correction, so it's a warning, not
 *     a hard validation error)
 *   Save is blocked until required-field/format errors are resolved,
 *   matching the same isInvalid + Feedback pattern already used
 *   throughout this codebase (TenantSignup.js, EmployeeList.js).
 *
 * Earlier fixes (unchanged):
 * 1. Plan dropdown pre-selects company's ACTUAL plan (subscription_plan_id) not first plan
 * 2. PATCH /superadmin/companies/:id with dynamic fields (no "replacement undefined" error)
 * 3. Promo code input when upgrading plan
 * 4. Invoice email sent to company admin on plan upgrade
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Badge, Button, Modal, Form, Row, Col,
    Spinner, InputGroup, Alert, Pagination,
} from 'react-bootstrap';
import {
    Building2, Search, Edit, RefreshCw, Plus,
    CheckCircle, XCircle, Tag, Mail,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
const fmtDate = (d) => !d ? '—'
    : new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
const fmt = (n) => n != null
    ? `₹${parseFloat(n).toLocaleString('en-IN')}` : '—';
const STATUS_COLORS = {
    active:     'success',
    inactive:   'secondary',
    suspended:  'danger',
    trial:      'warning',
    past_due:   'danger',
};
const SUB_STATUS_OPTS = ['trial','active','past_due','cancelled','suspended'];
const STATUS_OPTS     = ['active','inactive','suspended'];
// ── Phone validation — generic international, consistent with
// TenantSignup.js's isValidPhone (7-15 digits, optional leading +) ──────────
const isValidPhone = (raw) => {
    const cleaned = (raw || '').replace(/[\s\-()]/g, '');
    return /^\+?[0-9]{7,15}$/.test(cleaned);
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// ════════════════════════════════════════════════════════════════════════════
const ManageCompanies = () => {
    const [companies,  setCompanies]  = useState([]);
    const [plans,      setPlans]      = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [search,     setSearch]     = useState('');
    const [page,       setPage]       = useState(1);
    const [total,      setTotal]      = useState(0);
    const LIMIT = 15;
    // Edit modal
    const [showEdit,  setShowEdit]  = useState(false);
    const [editComp,  setEditComp]  = useState(null);
    const [editForm,  setEditForm]  = useState({});
    const [errors,    setErrors]    = useState({});
    const [saving,    setSaving]    = useState(false);
    // AUDIT TRAIL: subscription status change history for the company
    // currently open in the edit modal.
    const [auditHistory,     setAuditHistory]     = useState([]);
    const [auditLoading,     setAuditLoading]     = useState(false);
    const [showAuditHistory, setShowAuditHistory] = useState(false);
    // Promo for upgrade
    const [promoCode, setPromoCode] = useState('');
    const [promoData, setPromoData] = useState(null);
    const [promoLoading, setPromoLoading] = useState('');
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [compRes, planRes] = await Promise.all([
                axiosInstance.get('/superadmin/companies', {
                    params: { page, limit: LIMIT, search },
                }),
                axiosInstance.get('/superadmin/plans'),
            ]);
            setCompanies(compRes.data.data?.companies || []);
            setTotal(compRes.data.data?.pagination?.total || 0);
            setPlans(planRes.data.data || []);
        } catch (err) {
            toast.error('Failed to load companies');
        } finally { setLoading(false); }
    }, [page, search]);
    useEffect(() => { load(); }, [load]);
    // ── Open edit modal — pre-fill with ACTUAL company data ────────────────
    const openEdit = async (company) => {
        // Fetch full company data to get subscription_plan_id
        try {
            const res = await axiosInstance.get(`/superadmin/companies/${company.id}`);
            const c   = res.data.data;
            setEditComp(c);
            setEditForm({
                name:                 c.name || '',
                email:                c.email || '',
                phone:                c.phone || '',
                status:               c.status || 'active',
                subscription_plan_id: c.subscription_plan_id || '',  // ← KEY FIX: use actual plan ID
                subscription_status:  c.subscription_status || 'trial',
                subscription_ends_at: c.subscription_ends_at
                    ? c.subscription_ends_at.slice(0,10) : '',
                billing_cycle:        c.billing_cycle || 'monthly',
                notes:                c.notes || '',
            });
            setErrors({});
            setPromoCode('');
            setPromoData(null);
            // AUDIT TRAIL: reset history state for the newly-opened company —
            // loaded lazily only if the admin expands the History section
            // (see fetchAuditHistory), not eagerly on every modal open.
            setAuditHistory([]);
            setShowAuditHistory(false);
            setShowEdit(true);
        } catch {
            toast.error('Failed to load company details');
        }
    };
    // AUDIT TRAIL: lazy-loads subscription status change history for the
    // company currently open in the edit modal. Only fetched when the
    // admin actually expands the History section, to avoid an extra
    // request on every single edit-modal open when most edits won't need it.
    const fetchAuditHistory = async () => {
        if (!editComp?.id) return;
        setAuditLoading(true);
        try {
            const res = await axiosInstance.get(`/superadmin/companies/${editComp.id}/subscription-audit`);
            setAuditHistory(res.data.data || []);
        } catch {
            toast.error('Failed to load subscription history');
        } finally {
            setAuditLoading(false);
        }
    };
    const setF = (k, v) => {
        setEditForm(p => ({ ...p, [k]: v }));
        if (errors[k]) setErrors(p => ({ ...p, [k]: '' }));
    };
    // ── Validate edit form before saving ───────────────────────────────────
    const validateEditForm = () => {
        const e = {};
        if (!editForm.name?.trim()) e.name = 'Company name is required';
        const emailTrimmed = (editForm.email || '').trim();
        if (!emailTrimmed) e.email = 'Email is required';
        else if (!EMAIL_RE.test(emailTrimmed)) e.email = 'Enter a valid email address';
        if (editForm.phone?.trim() && !isValidPhone(editForm.phone)) {
            e.phone = 'Enter a valid phone number (7-15 digits, optional + country code)';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };
    // Non-blocking warning shown inline (not a hard validation error) when
    // subscription_ends_at is set to a date in the past — a SuperAdmin may
    // legitimately need to backdate this for a billing correction.
    const subscriptionDateInPast = editForm.subscription_ends_at
        && editForm.subscription_ends_at < new Date().toISOString().slice(0, 10);
    // ── Apply promo code ─────────────────────────────────────────────────────
    const applyPromo = async () => {
        if (!promoCode.trim()) return;
        setPromoLoading(true);
        try {
            const res = await axiosInstance.post('/public/validate-promo',
                { code: promoCode.trim().toUpperCase() });
            setPromoData(res.data.data);
            toast.success('Promo code applied!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Invalid promo code');
            setPromoData(null);
        } finally { setPromoLoading(false); }
    };
    // ── Detect upgrade/downgrade ─────────────────────────────────────────────
    const getPlanAction = () => {
        if (!editComp || !editForm.subscription_plan_id) return null;
        if (String(editForm.subscription_plan_id) === String(editComp.subscription_plan_id))
            return 'same';
        const oldPlan = plans.find(p => String(p.id) === String(editComp.subscription_plan_id));
        const newPlan = plans.find(p => String(p.id) === String(editForm.subscription_plan_id));
        if (!oldPlan || !newPlan) return null;
        const oldPrice = parseFloat(oldPlan.monthly_price || 0);
        const newPrice = parseFloat(newPlan.monthly_price || 0);
        return newPrice > oldPrice ? 'upgrade'
             : newPrice < oldPrice ? 'downgrade'
             : 'same';
    };
    // ── Calculate promo discount ─────────────────────────────────────────────
    const getPromoDiscount = (baseAmount) => {
        if (!promoData || !baseAmount) return 0;
        if (promoData.discount_type === 'percentage')
            return Math.round(baseAmount * promoData.discount_value / 100);
        return Math.min(promoData.discount_value, baseAmount);
    };
    // ── Save company ─────────────────────────────────────────────────────────
    const saveCompany = async () => {
        if (!validateEditForm()) return;
        setSaving(true);
        try {
            const payload = {
                name:                 editForm.name.trim(),
                email:                editForm.email.trim(),
                phone:                editForm.phone?.trim() || '',
                status:               editForm.status,
                subscription_plan_id: editForm.subscription_plan_id || null,
                subscription_status:  editForm.subscription_status,
                subscription_ends_at: editForm.subscription_ends_at || null,
                billing_cycle:        editForm.billing_cycle,
                notes:                editForm.notes,
            };
            // Add promo if applied
            if (promoData) payload.promo_code = promoData.code;
            const planAction = getPlanAction();
            if (planAction === 'upgrade') payload.plan_action = 'upgrade';
            await axiosInstance.patch(`/superadmin/companies/${editComp.id}`, payload);
            // If plan changed, notify and (optionally) send invoice
            if (planAction === 'upgrade' || planAction === 'downgrade') {
                const newPlan = plans.find(p => String(p.id) === String(editForm.subscription_plan_id));
                const baseAmt = parseFloat(
                    editForm.billing_cycle === 'yearly'
                        ? newPlan?.yearly_price : newPlan?.monthly_price
                ) || 0;
                const discount  = getPromoDiscount(baseAmt);
                const finalAmt  = baseAmt - discount;
                // Send invoice email via backend
                try {
                    await axiosInstance.post('/superadmin/companies/send-invoice', {
                        company_id:   editComp.id,
                        plan_name:    newPlan?.name || '',
                        billing_cycle: editForm.billing_cycle,
                        amount:       finalAmt,
                        promo_code:   promoData?.code || null,
                        discount_amount: discount,
                        action:       planAction,
                    });
                } catch {} // Don't block on email failure
                toast.success(
                    `Plan ${planAction === 'upgrade' ? 'upgraded' : 'downgraded'} successfully!` +
                    (finalAmt > 0 ? ` Invoice of ₹${finalAmt.toLocaleString('en-IN')} sent to company admin.` : '')
                );
            } else {
                toast.success('Company updated successfully!');
            }
            setShowEdit(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update company');
        } finally { setSaving(false); }
    };
    const totalPages = Math.ceil(total / LIMIT);
    const planAction = getPlanAction();
    const selectedNewPlan = plans.find(p => String(p.id) === String(editForm.subscription_plan_id));
    const baseAmt = parseFloat(
        editForm.billing_cycle === 'yearly'
            ? selectedNewPlan?.yearly_price : selectedNewPlan?.monthly_price
    ) || 0;
    const discount  = getPromoDiscount(baseAmt);
    const finalAmt  = baseAmt - discount;
    // ════════════════════════════════════════════════════════════════════════
    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Building2 size={22} className="text-primary"/>
                        Manage Companies
                    </h4>
                    <p className="text-muted small mb-0">{total} registered companies</p>
                </div>
                <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={load}>
                        <RefreshCw size={13} className="me-1"/>Refresh
                    </Button>
                </div>
            </div>
            {/* Search */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-3">
                    <InputGroup style={{maxWidth:360}}>
                        <InputGroup.Text><Search size={14}/></InputGroup.Text>
                        <Form.Control
                            placeholder="Search by name or email…"
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}/>
                        {search && (
                            <Button variant="outline-secondary"
                                onClick={() => { setSearch(''); setPage(1); }}>✕</Button>
                        )}
                    </InputGroup>
                </Card.Body>
            </Card>
            {/* Table */}
            <Card className="border-0 shadow-sm">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary"/>
                    </div>
                ) : (
                    <>
                        <Table hover responsive className="mb-0 align-middle small">
                            <thead className="bg-light">
                                <tr className="text-uppercase text-muted" style={{fontSize:10}}>
                                    <th className="ps-4">Company</th>
                                    <th>Plan</th>
                                    <th>Subscription</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th className="pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map(co => (
                                    <tr key={co.id}>
                                        <td className="ps-4">
                                            <div className="fw-medium">{co.name}</div>
                                            <div className="text-muted" style={{fontSize:10}}>
                                                {co.email}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg="primary" style={{fontSize:10}}>
                                                {co.plan_name || 'No Plan'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge
                                                bg={STATUS_COLORS[co.subscription_status] || 'secondary'}
                                                style={{fontSize:10}}>
                                                {co.subscription_status || '—'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge
                                                bg={co.status === 'active' ? 'success' : 'secondary'}
                                                style={{fontSize:10}}>
                                                {co.status}
                                            </Badge>
                                        </td>
                                        <td className="text-muted">{fmtDate(co.created_at)}</td>
                                        <td className="pe-4">
                                            <Button
                                                size="sm" variant="outline-primary"
                                                className="py-0 px-2"
                                                onClick={() => openEdit(co)}>
                                                <Edit size={12} className="me-1"/>Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {companies.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-5">
                                            No companies found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
                                <span className="text-muted small">
                                    Page {page} of {totalPages} · {total} total
                                </span>
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev disabled={page<=1} onClick={()=>setPage(p=>p-1)}/>
                                    {[...Array(Math.min(5,totalPages))].map((_,i)=>{
                                        const pg = Math.max(1,Math.min(page-2,totalPages-4))+i;
                                        return <Pagination.Item key={pg} active={pg===page}
                                            onClick={()=>setPage(pg)}>{pg}</Pagination.Item>;
                                    })}
                                    <Pagination.Next disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}/>
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </Card>
            {/* ── Edit Company Modal ─────────────────────────────────────── */}
            <Modal show={showEdit} onHide={()=>setShowEdit(false)} size="lg" centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                        <Edit size={16} className="text-primary"/>
                        Edit Company — {editComp?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Row className="g-3">
                        {/* Basic info */}
                        <Col xs={12}><div className="fw-bold small text-muted text-uppercase mb-1" style={{letterSpacing:1}}>Basic Info</div></Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Company Name *</Form.Label>
                            <Form.Control value={editForm.name||''} onChange={e=>setF('name',e.target.value)}
                                isInvalid={!!errors.name} style={{borderRadius:8}}/>
                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Email *</Form.Label>
                            <Form.Control type="email" value={editForm.email||''} onChange={e=>setF('email',e.target.value)}
                                isInvalid={!!errors.email} style={{borderRadius:8}}/>
                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Phone</Form.Label>
                            <Form.Control value={editForm.phone||''} onChange={e=>setF('phone',e.target.value)}
                                isInvalid={!!errors.phone} placeholder="+91 96118 92030" style={{borderRadius:8}}/>
                            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Company Status</Form.Label>
                            <Form.Select value={editForm.status||'active'} onChange={e=>setF('status',e.target.value)} style={{borderRadius:8}}>
                                {STATUS_OPTS.map(s=><option key={s} value={s}>{s}</option>)}
                            </Form.Select>
                        </Col>
                        {/* Subscription */}
                        <Col xs={12} className="mt-2">
                            <div className="fw-bold small text-muted text-uppercase mb-1" style={{letterSpacing:1}}>Subscription</div>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">
                                Plan
                                {editComp?.plan_name && (
                                    <span className="text-muted fw-normal ms-1">
                                        (Current: {editComp.plan_name})
                                    </span>
                                )}
                            </Form.Label>
                            <Form.Select
                                value={editForm.subscription_plan_id||''}
                                onChange={e=>{
                                    setF('subscription_plan_id', e.target.value);
                                    setPromoCode(''); setPromoData(null);
                                }}
                                style={{borderRadius:8}}>
                                <option value="">— No Plan —</option>
                                {plans.filter(p=>p.is_active).map(p=>(
                                    <option key={p.id} value={p.id}>
                                        {p.name} — ₹{parseFloat(p.monthly_price||0).toLocaleString('en-IN')}/mo
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Billing Cycle</Form.Label>
                            <Form.Select value={editForm.billing_cycle||'monthly'} onChange={e=>setF('billing_cycle',e.target.value)} style={{borderRadius:8}}>
                                <option value="monthly">Monthly</option>
                                <option value="yearly">Yearly</option>
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Subscription Status</Form.Label>
                            <Form.Select value={editForm.subscription_status||'trial'} onChange={e=>setF('subscription_status',e.target.value)} style={{borderRadius:8}}>
                                {SUB_STATUS_OPTS.map(s=><option key={s} value={s}>{s}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Subscription Ends At</Form.Label>
                            <Form.Control type="date" value={editForm.subscription_ends_at||''} onChange={e=>setF('subscription_ends_at',e.target.value)} style={{borderRadius:8}}/>
                            {subscriptionDateInPast && (
                                <div className="text-warning mt-1" style={{ fontSize: 11 }}>
                                    ⚠ This date is in the past — confirm this is intentional (e.g. a billing correction).
                                </div>
                            )}
                        </Col>
                        {/* Plan change summary */}
                        {planAction && planAction !== 'same' && (
                            <Col xs={12}>
                                <Alert variant={planAction==='upgrade'?'success':'warning'} className="py-2 small mb-0">
                                    {planAction==='upgrade' ? '⬆️ Upgrade' : '⬇️ Downgrade'}:{' '}
                                    <strong>{editComp?.plan_name || '—'}</strong>
                                    {' → '}
                                    <strong>{selectedNewPlan?.name || '—'}</strong>
                                    {baseAmt > 0 && (
                                        <span className="ms-2">
                                            · New price: {fmt(
                                                editForm.billing_cycle==='yearly'
                                                    ? selectedNewPlan?.yearly_price
                                                    : selectedNewPlan?.monthly_price
                                            )}/{editForm.billing_cycle==='yearly'?'yr':'mo'}
                                        </span>
                                    )}
                                    {planAction==='upgrade' && (
                                        <span className="ms-2 text-muted">
                                            (Invoice will be sent to company admin)
                                        </span>
                                    )}
                                </Alert>
                            </Col>
                        )}
                        {/* Promo code — only for upgrades */}
                        {planAction === 'upgrade' && (
                            <Col xs={12}>
                                <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                                    <Tag size={12} color="#d97706"/>
                                    Promo Code (optional)
                                </Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        placeholder="e.g. LAUNCH50, FREEDEMO"
                                        value={promoCode}
                                        onChange={e=>{
                                            setPromoCode(e.target.value.toUpperCase());
                                            if(promoData) setPromoData(null);
                                        }}
                                        onKeyDown={e=>e.key==='Enter'&&applyPromo()}
                                        style={{borderRadius:8, maxWidth:240, letterSpacing:1, fontWeight:600}}/>
                                    <Button variant="warning" onClick={applyPromo}
                                        disabled={promoLoading||!promoCode.trim()}
                                        style={{borderRadius:8}}>
                                        {promoLoading ? <Spinner size="sm"/> : 'Apply'}
                                    </Button>
                                    {promoData && (
                                        <Badge bg="success" className="d-flex align-items-center">
                                            <CheckCircle size={12} className="me-1"/>Applied
                                        </Badge>
                                    )}
                                </div>
                                {/* Promo summary */}
                                {promoData && baseAmt > 0 && (
                                    <div className="mt-2 p-2 rounded" style={{background:'#f0fdf4', fontSize:12}}>
                                        <div className="d-flex justify-content-between">
                                            <span>Base price:</span>
                                            <span>{fmt(baseAmt)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between text-success">
                                            <span>Promo discount ({promoCode}):</span>
                                            <span>-{fmt(discount)}</span>
                                        </div>
                                        <hr className="my-1"/>
                                        <div className="d-flex justify-content-between fw-bold">
                                            <span>Amount due:</span>
                                            <span style={{color: finalAmt===0?'#059669':'#1f2937'}}>
                                                {finalAmt===0 ? 'FREE' : fmt(finalAmt)}
                                            </span>
                                        </div>
                                        {promoData.trial_days > 0 && (
                                            <div className="text-success mt-1">
                                                🎁 +{promoData.trial_days}-day free trial
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Col>
                        )}
                        {/* Notes */}
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Notes (internal)</Form.Label>
                            <Form.Control as="textarea" rows={2} value={editForm.notes||''}
                                onChange={e=>setF('notes',e.target.value)}
                                placeholder="Internal notes about this company…"
                                style={{borderRadius:8}}/>
                        </Col>
                        {/* AUDIT TRAIL: subscription status change history —
                            collapsed by default, lazy-loaded on first expand */}
                        <Col xs={12}>
                            <Button
                                variant="link" size="sm"
                                className="p-0 text-decoration-none fw-bold"
                                style={{ fontSize: 12, color: '#6b7280' }}
                                onClick={() => {
                                    const next = !showAuditHistory;
                                    setShowAuditHistory(next);
                                    if (next && auditHistory.length === 0) fetchAuditHistory();
                                }}>
                                {showAuditHistory ? '▾' : '▸'} Subscription Status History
                            </Button>
                            {showAuditHistory && (
                                <div className="mt-2 p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                                    {auditLoading ? (
                                        <div className="text-center py-2"><Spinner size="sm"/></div>
                                    ) : auditHistory.length === 0 ? (
                                        <div className="text-muted small text-center py-2">
                                            No subscription status changes recorded for this company yet.
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                                            {auditHistory.map((a, i) => (
                                                <div key={a.id || i} className="d-flex justify-content-between align-items-start small"
                                                    style={{ borderBottom: i < auditHistory.length - 1 ? '1px solid #e5e7eb' : 'none', paddingBottom: 8 }}>
                                                    <div>
                                                        <Badge bg={STATUS_COLORS[a.old_status] || 'secondary'} style={{ fontSize: 10 }}>
                                                            {a.old_status || '—'}
                                                        </Badge>
                                                        <span className="mx-1 text-muted">→</span>
                                                        <Badge bg={STATUS_COLORS[a.new_status] || 'secondary'} style={{ fontSize: 10 }}>
                                                            {a.new_status}
                                                        </Badge>
                                                        {a.reason && (
                                                            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                                                "{a.reason}"
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="text-end text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                                        <div>{a.changed_by_name || 'Unknown'}</div>
                                                        <div>{fmtDate(a.changed_at)}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={()=>setShowEdit(false)}>Cancel</Button>
                    <Button variant="primary" onClick={saveCompany} disabled={saving}
                        className="d-flex align-items-center gap-2">
                        {saving ? <Spinner size="sm"/> : (
                            <>
                                {planAction==='upgrade' && <Mail size={14}/>}
                                {saving ? 'Saving…'
                                    : planAction==='upgrade' ? 'Upgrade & Send Invoice'
                                    : planAction==='downgrade' ? 'Downgrade Plan'
                                    : 'Save Changes'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
export default ManageCompanies;
